import React, { useState, FormEvent } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory, useLocation } from "react-router-dom";
import { doLogin } from '../../actions/auth/login'
import { connectApp, AppProp } from '../../reducers';
import { sendPasswordRecovery } from '../../actions/auth/passwordRecovery'
import ProgressButton from '../Utils/ProgressButton'
import ISCard from '../Utils/ISCard'
import { useTranslation } from 'react-i18next';
import { useThemeContext } from '../../theme/ThemeContext';
import { Suspense } from 'react';
import { ISProgress } from "../../components/ISProgress";
import { LocalStorage } from '../../localStorage';
import { CircularProgress, CssBaseline, ThemeProvider } from '@mui/material'
import { Grid, Typography, TextField, Button, Box, Link, Checkbox, FormControlLabel, IconButton, InputAdornment } from '@mui/material'
import { Visibility, VisibilityOff } from '@mui/icons-material';

type Props = AppProp<'session' | 'passwordRecovery'>

const Login: React.FC<Props> = ({ session, passwordRecovery }) => {
  const { theme } = useThemeContext()
  const dispatch = useDispatch()
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation(['login', 'menu']);

  const [email, setEmail] = useState<string | null>(LocalStorage.getLoginName())
  const [password, setPassword] = useState<string | null>(null)
  const [emailError, setEmailError] = useState<boolean>(false)
  const [checked, setChecked] = useState<boolean>(LocalStorage.getLoginName()!=='');
  const [loading, setLoading] = useState<boolean>(false)
  const [showPassword, setShowPassword] = useState<boolean>(false)

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleLogin = (event: FormEvent) => {
    event.preventDefault()
    if (checked)
      LocalStorage.saveLoginName(email)
    else 
      LocalStorage.saveLoginName('')
    //LocalStorage.saveClientId('0') // always data 0 when login
    setLoading(true)
    dispatch(doLogin(history, email, password, t('home', { ns: 'menu'})))
  }

  const handlePasswordRecovery = (event: FormEvent) => {
    event.preventDefault()
    if (!email) {
      setEmailError(true)
      return;
    }

    dispatch(sendPasswordRecovery(location.search, email, t('emailSent')))
    setEmailError(false)
  }

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  return (
    <Suspense fallback={<ISProgress/>}>
    <ThemeProvider theme={theme}>
    <CssBaseline/>
      <ISCard>
        <form onSubmit={handleLogin} style={{display:"flex", flexDirection:"column", minHeight: "500px", justifyContent: "space-between" }}>
          <Box display="flex" flexDirection="column">
          <Typography align="center" fontWeight='bold' variant="h3">
              {t('welcome')}
          </Typography>
          <Typography align="center" color="secondary" variant="h3">
              {t('portal')}
          </Typography>
          </Box>
          <Typography align="center" variant="h5">
              {t('login')}
          </Typography>
          <Box style={{display:"flex", flexDirection:"column"}}>
          <TextField 
            label={t('email')}
            margin="normal"
            variant="outlined"
            size='small'
            onChange={props => setEmail(props.target.value)}
            error={emailError}
            helperText={emailError ? t("emailError") : ""}
            InputProps={{ sx: { borderRadius: '8px' }}}
            InputLabelProps={{ shrink: true }}
            value={email}
            fullWidth
            required
          />
          <TextField
            label={t("password")}
            margin="normal"
            variant="outlined"
            size='small'
            autoComplete="on"
            type={showPassword ? 'text' : 'password'}
            InputProps={ { sx: { borderRadius: '8px' },       
                          endAdornment:
                            <InputAdornment position="end">
                              <IconButton color="secondary" 
                                aria-label="toggle password visibility"
                                onClick={handleClickShowPassword}
                                onMouseDown={handleMouseDownPassword}
                                edge="end"
                              >
                                {showPassword ? <VisibilityOff /> : <Visibility />}
                              </IconButton>
                            </InputAdornment>
                          }}
            InputLabelProps={{ shrink: true }}
            onChange={props => setPassword(props.target.value)}
            fullWidth
            required
          />
          {session.error ?
            <Typography align="center" variant="subtitle2">
              {session.error.message.toLowerCase().includes('wrong') ? t("wrong") : t("expired")}
          </Typography>
            : null}
           <FormControlLabel color="secondary" control={<Checkbox checked={checked}  onChange={handleChange}/>} label={<Typography variant="caption" color="primary">{t("rememberme")}</Typography>}/>
          </Box>
          <Button
            variant="contained"
            size="large"
            type="submit"
            sx={{textTransform:"none", borderRadius: '8px' }}
            fullWidth
          >
            {t("enter")}
          </Button>
          { loading && !session.error &&
            <div style={{display:'flex', justifyContent:'center'}}>
              <CircularProgress size={25} />
            </div>
          }

        </form>
        <ProgressButton
          state={passwordRecovery}
          variant="text"
          color="secondary"
          onClick={handlePasswordRecovery}
        >
          {t("forgot")}
        </ProgressButton>
        <Grid>
        <Link textAlign="center" color="secondary" href={`terms`} underline='hover' target="_blank">
            {t("terms")}
        </Link>
        </Grid>
      </ISCard>
    </ThemeProvider>
    </Suspense>
    )
}

export default connectApp('session', 'passwordRecovery')(Login)
